import lodash from 'lodash';
import format from 'date-fns/format';
import formatISO from 'date-fns/formatISO';
import { valueUtilities, rfidStrategy } from 'lib_ui-services';

const { get } = lodash;

export default function getColumnValue(
    record,
    columnHNode,
    { featureFlags, activeRecord, splashRecord, navigationSelection } = {}
) {
    let value = get(record, columnHNode.propertyName);
    if (columnHNode.displayProperties?.length) {
        let prefix = [columnHNode.namespace, columnHNode.relation].join(':');
        if (prefix) prefix += '.';

        value = columnHNode.displayProperties
            .map(property => get(record, `${prefix}${property}`))
            .filter(Boolean)
            .join(', ');
    }
    if (value == null && columnHNode.dataType !== 'mappedBoolean') {
        return '';
    }
    switch (columnHNode.dataType) {
        // A fraction is stored as the text it displays ("32 / 50"), so it needs no formatting
        // here. Sorting and exporting therefore keep working on the same string a Text column
        // would have produced; only the cell renders differently.
        case 'link':
        case 'text':
        case 'fraction':
            return value;
        case 'dateTime':
            if (columnHNode.format === 'ISO') return formatISO(new Date(value));
            return format(new Date(value), columnHNode.format);
        case 'localShortDate':
            return format(new Date(value), 'P');
        case 'localVerboseDateTime':
            return format(new Date(value), 'PP p');
        case 'encodedText':
            return rfidStrategy.getDisplayValue(value);
        case 'mappedBoolean':
            if (columnHNode.mappedTrueValue || columnHNode.mappedFalseValue) {
                return value ? columnHNode.mappedTrueValue : columnHNode.mappedFalseValue;
            } else {
                return value ? 'True' : 'False';
            }
        case 'gpsCoordinates':
            throw new Error('not implemented');
        case 'nestedDetail': {
            if (columnHNode.showStringValues === true && Array.isArray(value)) {
                const strings = value.filter(entry => typeof entry === 'string');
                if (columnHNode.showAllValues === true) return strings.join(', ');
                return strings.slice(0, 2).join(', ') + (strings.length > 2 ? ', ...' : '');
            }
            const nestedField = columnHNode.children?.find(c => c.hNodeTypeGroup === 'listColumn');
            if (!nestedField) return '';
            const values = getNestedValues(value, nestedField, {
                featureFlags,
                activeRecord,
                splashRecord,
                navigationSelection
            });
            if (columnHNode.showAllValues === true) return values.join(', ');
            return values.slice(0, 2).join(', ') + (values.length > 2 ? ', ...' : '');
        }
        case 'patchDetail':
            throw new Error('not implemented');
        // TODO: not sure what this is yet.
        // return value;
        case 'currency':
            return valueUtilities.formatCurrency(value);
        default:
            throw new Error(`Unknown data type for column ${columnHNode.label}`);
    }
}

/**
 * Retrieves nested values from an object or array of objects.
 *
 * @param {Object|Array} value - The object or array of objects to retrieve the nested values from.
 * @param {string} nestedField - The field name to retrieve the value from.
 * @param {Object} context - Additional context that might be needed for value retrieval.
 * @returns {Array} - An array of unique nested values.
 */
export function getNestedValues(value, nestedField, context) {
    if (Array.isArray(value)) {
        if (!value.length) return [];

        const values = [...new Set(value.map(entry => getColumnValue(entry, nestedField, context)).filter(x => !!x))];
        return values;
    }
    return [getColumnValue(value, nestedField, context)];
}
